import { type FC, type MouseEvent } from 'react';
import { useDispatch } from 'react-redux';
import { defineMessages, useIntl } from 'dibs-react-intl';
import { useFragment, graphql } from 'react-relay';
import {
    getPreviouslyAppliedCategory,
    getBackToCategory,
    getCurrentCategory,
    shouldShowBackToAllCategories,
} from '../../../utils/categoryHelpers.ts';
import { updateUriRef } from '../../../actions/filterActions.ts';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext.tsx';

// components
import { SbRespRefineMenuLinkList } from '../SbRespRefineMenuLinkList/SbRespRefineMenuLinkList.tsx';
import { SbSharedRefineMenuLink } from '../../SbSharedRefineMenu/SbSharedRefineMenuLink/SbSharedRefineMenuLink.tsx';

import {
    type SbRespRefineMenuFilterCategory_itemSearch$data,
    type SbRespRefineMenuFilterCategory_itemSearch$key,
} from './__generated__/SbRespRefineMenuFilterCategory_itemSearch.graphql.ts';
import { type SbRespRefineMenuFilterCategory_filters$key } from './__generated__/SbRespRefineMenuFilterCategory_filters.graphql.ts';

const itemSearchFragment = graphql`
    fragment SbRespRefineMenuFilterCategory_itemSearch on ItemSearchQueryConnection {
        pageType
        totalResults
        isAttributePage
        appliedFilters {
            name
            localizedFilterName
            canBeDismissed
            values {
                urlLabel
                linkReference
                count
                displayName
            }
        }
        searchResultData {
            vectorResultCount
        }
    }
`;
const filtersFragment = graphql`
    fragment SbRespRefineMenuFilterCategory_filters on SearchBrowseFilter @relay(plural: true) {
        name
        values {
            count
            displayName
            linkReference
            urlLabel
            linkable
        }
    }
`;

const messages = defineMessages({
    all: {
        id: 'sb.SbRespRefineMenuFilterCategory.all',
        defaultMessage: 'All',
    },
    backToAll: {
        id: 'sb.SbRespRefineMenuFilterCategory.backToAll',
        defaultMessage: 'Back To All Categories',
    },
    backTo: {
        id: 'sb.SbRespRefineMenuFilterCategory.backTo',
        defaultMessage: 'Back To {backToCategory}',
    },
});

type CategoryFilter = Pick<
    NonNullable<
        NonNullable<SbRespRefineMenuFilterCategory_itemSearch$data['appliedFilters']>[number]
    >,
    'name' | 'values'
>;

type Props = {
    itemSearch: SbRespRefineMenuFilterCategory_itemSearch$key;
    filters: SbRespRefineMenuFilterCategory_filters$key;
    shouldUseExpandableList?: boolean;
    trackingOptions?: Record<string, unknown>;
};

export const SbRespRefineMenuFilterCategory: FC<Props> = ({
    itemSearch: itemSearchRef,
    filters: filtersRef,
    shouldUseExpandableList = true,
    trackingOptions,
}) => {
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const filters = useFragment(filtersFragment, filtersRef);
    const dispatch = useDispatch();
    const { formatMessage } = useIntl();
    const { appliedFilters, totalResults, pageType, isAttributePage } = itemSearch;
    const previouslyAppliedCategory = getPreviouslyAppliedCategory(appliedFilters);
    const backToCategory = getBackToCategory(appliedFilters);
    const currentCategory = getCurrentCategory<CategoryFilter>(appliedFilters, filters);
    const showBackToAllCategories = shouldShowBackToAllCategories(
        { pageType, isAttributePage },
        filters
    );
    const { isL3CategoryFilterDisplayVariant } = useServerVarsContext();

    const { name, values } = currentCategory || {};
    const shouldIndent = !!backToCategory || (!backToCategory && showBackToAllCategories);

    const { linkReference, displayName } = previouslyAppliedCategory?.values?.[0] || {};
    const backToCategoryValue = backToCategory?.values?.[0]?.displayName;
    const hasVectorSearchResults = !!itemSearch?.searchResultData?.vectorResultCount;
    const sharedMenuLinkProps = {
        onClick: (event: MouseEvent) => {
            event.preventDefault();
            dispatch(
                updateUriRef({
                    filterName: previouslyAppliedCategory?.name || '',
                    filterValue: previouslyAppliedCategory?.values?.[0],
                    isDeselected: true,
                    event,
                    trackingOptions,
                })
            );
        },
        linkReference,
        linkable: false,
        hasVectorSearchResults,
    };

    if (previouslyAppliedCategory) {
        const previouslyAppliedCategoryUrlLabel = previouslyAppliedCategory.values?.[0]?.urlLabel;
        return (
            <>
                {/* Back to all categories link - only shown on non-Browse pages */}
                {!backToCategory && showBackToAllCategories && (
                    <SbSharedRefineMenuLink
                        {...sharedMenuLinkProps}
                        text={formatMessage(messages.backToAll)}
                        dataTn="back-to-all"
                    />
                )}
                {/* Back to category Link */}
                {!!backToCategory && (
                    <SbSharedRefineMenuLink
                        {...sharedMenuLinkProps}
                        text={formatMessage(messages.backTo, {
                            backToCategory: backToCategoryValue,
                        })}
                        dataTn={`back-to-${backToCategoryValue}`}
                    />
                )}
                {/* current category */}
                <SbSharedRefineMenuLink
                    selected
                    count={totalResults}
                    shouldIndent={shouldIndent}
                    onClick={e => e.preventDefault()}
                    text={displayName || ''}
                    linkReference={linkReference}
                    hasVectorSearchResults={hasVectorSearchResults}
                />
                {/* filter options */}
                {previouslyAppliedCategory.name !== name && (
                    <SbRespRefineMenuLinkList
                        values={values}
                        filterName={name || ''}
                        shouldIndent={shouldIndent}
                        shouldUseExpandableList={shouldUseExpandableList}
                        trackingOptions={trackingOptions}
                        hasVectorSearchResults={hasVectorSearchResults}
                    />
                )}
                {/* current category siblings */}
                {isL3CategoryFilterDisplayVariant && previouslyAppliedCategory.name === name && (
                    <SbRespRefineMenuLinkList
                        values={(values || []).filter(
                            value => value?.urlLabel !== previouslyAppliedCategoryUrlLabel
                        )}
                        filterName={name || ''}
                        shouldIndent={shouldIndent}
                        shouldUseExpandableList={shouldUseExpandableList}
                        trackingOptions={trackingOptions}
                        hasVectorSearchResults={hasVectorSearchResults}
                    />
                )}
            </>
        );
    } else {
        return (
            <>
                <SbSharedRefineMenuLink
                    text={formatMessage(messages.all)}
                    selected
                    linkReference="/search"
                    count={totalResults}
                    dataTn="all-categories"
                    onClick={e => e.preventDefault()}
                    hasVectorSearchResults={hasVectorSearchResults}
                />
                <SbRespRefineMenuLinkList
                    values={values}
                    filterName={name || ''}
                    trackingOptions={trackingOptions}
                    hasVectorSearchResults={hasVectorSearchResults}
                />
            </>
        );
    }
};
